import { createFileRoute, Link } from "@tanstack/react-router";
import { useId, useState } from "react";
import { Logo } from "@/components/brand/logo";
import { BrandButton } from "@/components/brand/brand-button";
import { authClient } from "../lib/auth-client";

export const Route = createFileRoute("/forgot-password")({
	component: ForgotPasswordPage,
});

function ForgotPasswordPage() {
	const [email, setEmail] = useState("");
	const [error, setError] = useState<string | null>(null);
	const [loading, setLoading] = useState(false);
	const [emailSent, setEmailSent] = useState(false);
	const emailId = useId();

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		setError(null);
		setLoading(true);

		try {
			const { error: apiError } = await authClient.requestPasswordReset({
				email,
				redirectTo: "/reset-password",
			});
			if (apiError) {
				setError(apiError.message || "Failed to send reset email");
			} else {
				setEmailSent(true);
			}
		} catch (err) {
			setError(
				err instanceof Error ? err.message : "Failed to send reset email",
			);
		} finally {
			setLoading(false);
		}
	};

	return (
		<main
			id="main-content"
			className="min-h-svh flex items-center justify-center px-4"
			style={{ backgroundColor: "#0a0a0a" }}
		>
			<div className="w-full max-w-md">
				<div className="text-center mb-8">
					<Logo size="sm" />
				</div>

				<div
					className="p-8"
					style={{ backgroundColor: "#111111", border: "1px solid rgba(255,255,255,0.08)", borderTop: "3px solid #6B3FF2" }}
				>
					{emailSent ? (
						<div className="text-center space-y-4">
							<div
								className="w-16 h-16 flex items-center justify-center mx-auto"
								style={{ backgroundColor: "rgba(43,154,102,0.1)" }}
							>
								<span
									className="material-symbols-outlined"
									aria-hidden="true"
									style={{ fontSize: "32px", color: "#2b9a66", fontVariationSettings: "'FILL' 1" }}
								>
									mark_email_read
								</span>
							</div>
							<h1 className="text-4xl font-semibold tracking-tight" style={{ color: "#ffffff", fontFamily: "'Geist', -apple-system, sans-serif" }}>
								Check your email
							</h1>
							<p className="text-sm" style={{ color: "#888888" }}>
								We sent a reset link to{" "}
								<span className="font-bold" style={{ color: "#ffffff" }}>
									{email}
								</span>
							</p>

							<div className="pt-2 space-y-3">
								<p className="text-sm" style={{ color: "#888888" }}>
									Didn&apos;t receive it?{" "}
									<button
										type="button"
										onClick={() => setEmailSent(false)}
										className="font-bold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0072f5]/60"
										style={{ color: "#6B3FF2" }}
										onMouseEnter={e => { (e.currentTarget as HTMLElement).style.color = "#ffffff"; }}
										onMouseLeave={e => { (e.currentTarget as HTMLElement).style.color = "#6B3FF2"; }}
										onFocus={e => { (e.currentTarget as HTMLElement).style.color = "#ffffff"; }}
										onBlur={e => { (e.currentTarget as HTMLElement).style.color = "#6B3FF2"; }}
									>
										Try again
									</button>
								</p>
								<BrandButton asChild variant="outline" className="w-full flex items-center gap-2">
									<Link to="/login">
										<span className="material-symbols-outlined" aria-hidden="true" style={{ fontSize: "16px" }}>arrow_back</span>
										Back to login
									</Link>
								</BrandButton>
							</div>
						</div>
					) : (
						<>
							<div className="text-center mb-6">
								<p className="text-[10px] font-bold uppercase tracking-[0.1em] mb-2" style={{ color: "#6B3FF2", fontFamily: "inherit" }}>Account Access</p>
								<h1
									id="forgot-heading"
									className="text-4xl font-semibold tracking-tight"
									style={{ color: "#ffffff", fontFamily: "'Geist', -apple-system, sans-serif" }}
								>
									Reset password
								</h1>
								<p className="mt-2 text-sm" style={{ color: "#888888" }}>
									Enter your email and we&apos;ll send you a reset link.
								</p>
							</div>

							{error && (
								<div
									role="alert"
									className="mb-4 px-4 py-3 text-sm"
									style={{
										border: "1px solid rgba(255,110,132,0.2)",
										borderLeft: "3px solid #ff6e84",
										backgroundColor: "rgba(255,110,132,0.08)",
										color: "#ff6e84",
									}}
								>
									{error}
								</div>
							)}

							<form onSubmit={handleSubmit} aria-labelledby="forgot-heading" className="space-y-5">
								<div>
									<label
										htmlFor={emailId}
										className="block text-sm font-bold mb-1.5"
										style={{ color: "#ffffff" }}
									>
										Email
									</label>
									<div className="relative">
										<span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2" aria-hidden="true" style={{ fontSize: "16px", color: "#888888" }}>mail</span>
										<input
											id={emailId}
											type="email"
											autoComplete="email"
											value={email}
											onChange={(e) => setEmail(e.target.value)}
											required
											className="w-full pl-10 pr-3 py-2.5 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0072f5]/50"
											style={{
												border: "1px solid rgba(255,255,255,0.1)",
												backgroundColor: "#0a0a0a",
												color: "#ffffff",
											}}
											placeholder="name@company.com"
										/>
									</div>
								</div>

								<BrandButton
									type="submit"
									disabled={loading}
									aria-busy={loading}
									className="w-full"
									size="lg"
								>
									{loading ? "Sending..." : "Send reset link"}
								</BrandButton>
							</form>

							<p className="mt-6 text-center text-sm" style={{ color: "#888888" }}>
								Remember your password?{" "}
								<Link
									to="/login"
									className="font-bold transition-colors"
									style={{ color: "#6B3FF2" }}
									onMouseEnter={e => { (e.currentTarget as HTMLElement).style.color = "#ffffff"; }}
									onMouseLeave={e => { (e.currentTarget as HTMLElement).style.color = "#6B3FF2"; }}
									onFocus={e => { (e.currentTarget as HTMLElement).style.color = "#ffffff"; }}
									onBlur={e => { (e.currentTarget as HTMLElement).style.color = "#6B3FF2"; }}
								>
									Log in
								</Link>
							</p>
						</>
					)}
				</div>
			</div>
		</main>
	);
}
